import { DataProvider } from '@refinedev/core'

import axios from 'axios'
import queryString from 'query-string'

const apiUrl = import.meta.env.VITE_API_URL

/**
 * The pagination total from the X-Total-Count header, or undefined when it is
 * unreadable. Never NaN.
 *
 * `Number(undefined)` is NaN, and a NaN total reaches MUI's TablePagination as
 * "1-NaN of NaN" with the next-page arrow stuck enabled. The header goes missing
 * whenever it is absent from the API's CORS expose_headers - the API sends it, the
 * browser just cannot read it cross-origin (see expose_headers in app/api/server.py).
 * Falls back to the row count when the payload is a list, so a degraded response
 * reports what it actually has rather than a number that poisons arithmetic.
 */
const totalFrom = (
  headers: Record<string, unknown> | undefined,
  data?: unknown
): number | undefined => {
  const parsed = Number(headers?.['x-total-count'])
  if (Number.isFinite(parsed)) {
    return parsed
  }
  return Array.isArray(data) ? data.length : undefined
}

/**
 * As totalFrom, but for refine's getList, whose contract requires a number.
 * The payload there is always a list, so the row count is a truthful floor.
 */
const totalForList = (
  headers: Record<string, unknown> | undefined,
  data: unknown
): number => totalFrom(headers, data) ?? (Array.isArray(data) ? data.length : 0)

export const dataProvider: DataProvider = {
  custom: async ({ url, method, filters, payload, query, headers }) => {
    let requestUrl = `${apiUrl}${url}`
    if (query) {
      requestUrl = `${requestUrl}?${queryString.stringify(query)}`
    }

    // Capacity changes return immediately; never leave their dialogs waiting forever.
    const requestOptions = { headers, ...(url.startsWith('/llm-work/pools') ? { timeout: 20000 } : {}) }
    let axiosResponse
    switch (method) {
      case 'get':
        axiosResponse = await axios.get(requestUrl, requestOptions)
        break
      case 'post':
        axiosResponse = await axios.post(requestUrl, payload, requestOptions)
        break
      case 'put':
        axiosResponse = await axios.put(requestUrl, payload, { headers })
        break
      case 'patch':
        axiosResponse = await axios.patch(requestUrl, payload, { headers })
        break
      case 'delete':
        axiosResponse = await axios.delete(requestUrl, {
          headers,
          data: payload,
        })
        break
      default:
        axiosResponse = await axios.get(requestUrl, requestOptions)
        break
    }

    return {
      data: axiosResponse.data,
      total: totalFrom(axiosResponse.headers, axiosResponse.data),
    }
  },

  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    // No special handling needed for feedback - the base API URL already includes /admin

    // CRITICAL: Handle charges-payments FIRST before any other logic
    if (
      resource === 'charges-payments' ||
      resource.includes('charges-payments')
    ) {
      // Extract org_uuid from filters or meta
      let orgUuid = meta?.org_uuid

      // Check if org_uuid is in filters array
      if (!orgUuid && filters && filters.length > 0) {
        const orgFilter = filters.find((f: any) => f.field === 'org_uuid')
        if (orgFilter) {
          orgUuid = orgFilter.value
        }
      }

      if (!orgUuid) {
        // Return empty data instead of throwing to prevent fallthrough
        return {
          data: [],
          total: 0,
        }
      }

      const params: any = {
        current: pagination?.current,
        pageSize: pagination?.pageSize,
      }

      // Add meta params (type_filter, search) to query params
      if (meta?.type_filter) {
        params.type_filter = meta.type_filter
      }
      if (meta?.search) {
        params.search = meta.search
      }

      const url = `${apiUrl}/orgs/${orgUuid}/charges-payments?${queryString.stringify(
        params
      )}`
      const response = await axios.get(url)
      const total = totalForList(response.headers, response.data)

      return {
        data: response.data.data || response.data,
        total,
        balance_outstanding: response.data.balance_outstanding,
      }
    }

    // Handle billing-history resource
    if (
      resource === 'billing-history' ||
      resource.includes('billing-history')
    ) {
      // Extract org_uuid from filters or meta
      let orgUuid = meta?.org_uuid

      // Check if org_uuid is in filters array
      if (!orgUuid && filters && filters.length > 0) {
        const orgFilter = filters.find((f: any) => f.field === 'org_uuid')
        if (orgFilter) {
          orgUuid = orgFilter.value
        }
      }

      if (!orgUuid) {
        // Return empty data instead of throwing to prevent fallthrough
        return {
          data: [],
          total: 0,
        }
      }

      const params: any = {
        current: pagination?.current,
        pageSize: pagination?.pageSize,
      }

      // Add meta params (type_filter, search) to query params
      if (meta?.type_filter) {
        params.type_filter = meta.type_filter
      }
      if (meta?.search) {
        params.search = meta.search
      }

      const url = `${apiUrl}/orgs/${orgUuid}/billing-history?${queryString.stringify(
        params
      )}`
      const response = await axios.get(url)
      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }

    if (resource === 'funder-options') {
      const response = await axios.get(`${apiUrl}/funders/options/`)
      return {
        data: response.data,
        total: response.data.length,
      }
    }

    if (resource === 'opportunity_categories') {
      const response = await axios.get(`${apiUrl}/opportunities/categories`)
      return {
        data: response.data,
        total: response.data.length,
      }
    }

    if (resource === 'accounts') {
      const response = await axios.get(`${apiUrl}/${resource}/summary`)
      return {
        data: response.data.data,
        total: 1,
      }
    }

    if (resource === 'discount-codes') {
      const params: any = {
        current: pagination?.current,
        pageSize: pagination?.pageSize,
      }

      if (meta?.search) {
        params.search = meta.search
      }
      if (meta?.filter) {
        params.filter = meta.filter
      }
      if (meta?.sort_by) {
        params.sort_by = meta.sort_by
      }
      if (meta?.sort_order) {
        params.sort_order = meta.sort_order
      }
      if (meta?.show_archived !== undefined) {
        params.show_archived = meta.show_archived
      }

      const url = `${apiUrl}/${resource}/?${queryString.stringify(params)}`
      const response = await axios.get(url)
      return {
        data: response.data,
        total: totalForList(response.headers, response.data),
      }
    }

    if (resource === 'discount-codes/active-orgs') {
      const params: any = {
        current: pagination?.current,
        pageSize: pagination?.pageSize,
      }

      if (meta?.discount_code_id) {
        params.discount_code_id = meta.discount_code_id
      }

      const url = `${apiUrl}/${resource}/?${queryString.stringify(params)}`
      const response = await axios.get(url)
      return {
        data: response.data,
        total: totalForList(response.headers, response.data),
      }
    }

    const url = `${apiUrl}/${resource}/?${queryString.stringify({
      ...pagination,
      sorters: JSON.stringify(sorters),
      filters: JSON.stringify(filters),
    })}`

    if (resource === 'billing/tracking') {
      const filterParams =
        filters?.reduce((acc: any, filter: any) => {
          if (
            filter.field &&
            filter.value !== undefined &&
            filter.value !== ''
          ) {
            acc[filter.field] = filter.value
          }
          return acc
        }, {}) || {}

      const queryParams = queryString.stringify({
        ...pagination,
        ...filterParams,
      })
      const response = await axios.get(`${apiUrl}/${resource}?${queryParams}`)
      return {
        data: [response.data], // Wrap in array as expected by refine
        total: response.data.total_count || 0,
      }
    }

    if (resource === 'xero/status') {
      const response = await axios.get(`${apiUrl}/${resource}`)
      return {
        data: [response.data], // Wrap in array as expected by refine
        total: 1,
      }
    }

    if (resource === 'regions' && meta?.paginate === false) {
      const response = await axios.get(`${apiUrl}/${resource}/`, {
        params: { paginate: false },
      })
      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }
    if (resource === 'region-sets') {
      const response = await axios.get(`${apiUrl}/regions/sets`, {
        params: { paginate: false },
      })
      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }

    if (resource === 'iwi' && meta?.paginate === false) {
      const response = await axios.get(`${apiUrl}/${resource}/`, {
        params: { paginate: false },
      })
      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }

    if (resource === 'opportunities' && meta?.isTestFiltered === true) {
      const url = `${apiUrl}/${resource}/test-filtered`

      const response = await axios.post(
        url,
        {
          current: pagination?.current,
          pageSize: pagination?.pageSize,
          sorters: JSON.stringify(sorters),
          filters: JSON.stringify(filters),
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }

    if (resource === 'assigned-opportunities' && meta?.get_users) {
      const response = await axios.get(`${apiUrl}/${resource}/users`)

      return {
        data: response.data,
        total: totalForList(response.headers, response.data),
      }
    }
    if (resource === 'assigned-funders' && meta?.get_users) {
      const response = await axios.get(`${apiUrl}/${resource}/users`)

      return {
        data: response.data,
        total: totalForList(response.headers, response.data),
      }
    }

    if (resource === 'assigned-opportunities' && meta?.user_uuid) {
      const user_uuid = meta.user_uuid.data.toString()
      const url = `${apiUrl}/${resource}/users/${user_uuid}?${queryString.stringify(
        {
          ...pagination,
          sorters: JSON.stringify(sorters),
          filters: JSON.stringify(filters),
        }
      )}`

      const response = await axios.get(url)

      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }

    if (resource === 'assigned-funders' && meta?.user_uuid) {
      const user_uuid = meta.user_uuid.data.toString()
      const url = `${apiUrl}/${resource}/users/${user_uuid}?${queryString.stringify(
        {
          ...pagination,
          sorters: JSON.stringify(sorters),
          filters: JSON.stringify(filters),
        }
      )}`

      const response = await axios.get(url)

      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }

    if (resource === 'assigned-opportunities') {
      const response = await axios.get(`${apiUrl}/${resource}/opportunities`)

      return {
        data: response.data,
        total: totalForList(response.headers, response.data),
      }
    }

    if (resource === 'assigned-funders') {
      const response = await axios.get(`${apiUrl}/${resource}/funders`)

      return {
        data: response.data,
        total: totalForList(response.headers, response.data),
      }
    }

    if (resource === 'users' && typeof meta?.search === 'string') {
      const response = await axios.get(
        `${apiUrl}/${resource}/?${queryString.stringify({
          ...pagination,
          sorters: JSON.stringify(sorters),
          filters: JSON.stringify(filters),
          q: meta.search,
        })}`
      )

      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }

    if (resource === 'opportunities' && meta?.testFilters) {
      const params = new URLSearchParams({
        ...(meta.amount !== undefined &&
          meta.amount > 0 && { amount: meta.amount.toString() }),
        ...(meta.region !== undefined &&
          meta.region !== null && { region_id: meta.region.toString() }),
        ...(meta.funding_required_by && {
          funding_required_by: meta.funding_required_by,
        }),
        // Hard filter parameters
        ...(meta.is_registered !== undefined && {
          is_registered: meta.is_registered.toString(),
        }),
        ...(meta.accepts_gambling_proceeds !== undefined && {
          accepts_gambling_proceeds: meta.accepts_gambling_proceeds.toString(),
        }),
        ...(meta.accepts_international !== undefined && {
          accepts_international: meta.accepts_international.toString(),
        }),
        ...((meta.iwi !== undefined || meta.iwi?.length > 0) && {
          iwi: meta.iwi.toString(),
        }),
        ...(meta.only_funds_registered_charities !== undefined && {
          only_funds_registered_charities:
            meta.only_funds_registered_charities.toString(),
        }),
        ...(meta.include_relational !== undefined && {
          include_relational: meta.include_relational.toString(),
        }),
      })

      const response = await axios.get(
        `${apiUrl}/${resource}/test-filters?${params}`,
        {
          params: { paginate: false },
        }
      )

      return {
        data: response.data,
        total: totalForList(response.headers, response.data),
      }
    }

    if (resource === 'funding-rounds' && meta?.opportunity_uuid) {
      const response = await axios.get(
        `${apiUrl}/${resource}/${meta.opportunity_uuid}`
      )
      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }
    if (resource === 'funder-revisions' && meta?.funder_uuid) {
      const response = await axios.get(
        `${apiUrl}/${resource}/by-funder-id/${meta.funder_uuid}`
      )
      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }
    if (resource === 'opportunity-revisions' && meta?.opportunity_uuid) {
      const response = await axios.get(
        `${apiUrl}/${resource}/by-opportunity-id/${meta.opportunity_uuid}`
      )
      return {
        data: response.data,
        total: Number(response.data.total),
      }
    }
    if (resource === 'discount-code-revisions' && meta?.discount_code_id) {
      const response = await axios.get(
        `${apiUrl}/discount-codes/revisions/${meta.discount_code_id}`
      )
      return {
        data: response.data,
        total: Number(response.data.total),
      }
    }

    if (resource === 'users-orgs' && meta?.org_uuid) {
      const response = await axios.get(`${apiUrl}/${resource}/${meta.org_uuid}`)
      return {
        data: response.data,
        total: Number(response.data.total),
      }
    }
    if (resource === 'user-organisations' && meta?.user_uuid) {
      const response = await axios.get(
        `${apiUrl}/users/${meta.user_uuid}/organisations`
      )
      return {
        data: response.data,
        total: response.data.length,
      }
    }
    if (resource === 'org-children' && meta?.org_uuid) {
      const response = await axios.get(
        `${apiUrl}/orgs/${resource}/${meta.org_uuid}`
      )
      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }

    // Handle org docs endpoint
    if (resource === 'orgs' && meta?.orgUuid && meta?.endpoint === 'docs') {
      const response = await axios.get(`${apiUrl}/orgs/${meta.orgUuid}/docs`)
      return {
        data: response.data,
        total: response.data.length,
      }
    }

    // Handle funding needs by org endpoint
    if (resource === 'funding-needs/organisation' && meta?.orgUuid) {
      const response = await axios.get(
        `${apiUrl}/funding-needs/organisation/${meta.orgUuid}`
      )
      return {
        data: response.data,
        total: response.data.length,
      }
    }

    // Handle applications by org endpoint
    if (resource === 'applications/org' && meta?.orgUuid) {
      const response = await axios.get(
        `${apiUrl}/applications/organisation/${meta.orgUuid}`
      )
      return {
        data: response.data,
        total: response.data.length,
      }
    }

    // Handle org regions endpoint
    if (resource === 'orgs' && meta?.orgUuid && meta?.endpoint === 'regions') {
      const response = await axios.get(`${apiUrl}/orgs/${meta.orgUuid}/regions`)
      return {
        data: response.data,
        total: response.data.length,
      }
    }

    if (resource === 'child-org-options') {
      const response = await axios.get(`${apiUrl}/orgs/${resource}`)
      const total = totalForList(response.headers, response.data)
      return {
        data: response.data,
        total,
      }
    }

    if (resource === 'org-activity') {
      const params: any = {
        ...pagination,
        sorters: JSON.stringify(sorters),
        filters: JSON.stringify(filters),
      }

      // Add activity_type filter from meta
      if (meta?.activityType && meta.activityType !== 'all') {
        params.activity_type = meta.activityType
      }

      const url = `${apiUrl}/orgs/activity?${queryString.stringify(params)}`
      const response = await axios.get(url)

      // Handle new response structure with typeCounts
      if (response.data.data) {
        // New structure with data, total, and typeCounts
        return {
          data: response.data.data,
          total: response.data.total,
          typeCounts: response.data.typeCounts,
        }
      } else {
        // Fallback for old structure (backward compatibility)
        const total = totalForList(response.headers, response.data)
        return {
          data: response.data,
          total,
        }
      }
    }
    if (resource === 'upcoming-renewals') {
      const url = `${apiUrl}/orgs/upcoming-renewals`

      const response = await axios.get(url)

      const total = response.data.length
      return {
        data: response.data,
        total,
      }
    }

    if (resource === 'memories' && meta?.orgUuid) {
      const mergedFilters = [
        ...(filters || []),
        {
          field: 'org_uuid',
          operator: 'eq',
          value: meta.orgUuid,
        },
      ]

      const url = `${apiUrl}/${resource}/?${queryString.stringify({
        ...pagination,
        sorters: JSON.stringify(sorters),
        filters: JSON.stringify(mergedFilters),
      })}`
      const response = await axios.get(url)
      return {
        data: response.data,
        total: totalForList(response.headers, response.data),
      }
    }

    if (resource === 'memory-activity' && meta?.orgUuid) {
      const mergedFilters = [
        ...(filters || []),
        {
          field: 'org_uuid',
          operator: 'eq',
          value: meta.orgUuid,
        },
      ]

      const url = `${apiUrl}/${resource}/?${queryString.stringify({
        ...pagination,
        sorters: JSON.stringify(sorters),
        filters: JSON.stringify(mergedFilters),
      })}`
      const response = await axios.get(url)
      return {
        data: response.data,
        total: totalForList(response.headers, response.data),
      }
    }

    const response = await axios.get(url)

    const total = totalForList(response.headers, response.data)
    return {
      data: response.data,
      total,
    }
  },

  getOne: async ({ resource, id }) => {
    if (resource === 'user-by-email') {
      const response = await axios.get(`${apiUrl}/users/by-email/${id}`)
      return {
        data: response.data,
      }
    }
    if (resource === 'assigned-opportunities') {
      const response = await axios.get(`${apiUrl}/${resource}/${id}`)
      return {
        data: response.data,
      }
    }
    if (resource === 'assigned-funders') {
      const response = await axios.get(`${apiUrl}/${resource}/${id}`)
      return {
        data: response.data,
      }
    }
    if (resource === 'account') {
      const response = await axios.get(`${apiUrl}/orgs/${resource}/${id}`)
      return {
        data: response.data,
      }
    }

    if (resource === 'org-activity') {
      const response = await axios.get(`${apiUrl}/orgs/activity/${id}`)
      return {
        data: response.data,
        total: response.data.length,
      }
    }

    const response = await axios.get(`${apiUrl}/${resource}/${id}`)
    return {
      data: response.data,
    }
  },

  // getMany: async ({ resource, ids }) => {
  //   const response = await axios.get(
  //     `${apiUrl}/${resource}/?id=${ids.join(',')}`
  //   )
  //   return {
  //     data: response.data,
  //   }
  // },

  create: async ({ resource, variables, meta }) => {
    if (resource === 'opportunities' && meta?.copy_from_uuid) {
      const response = await axios.post(
        `${apiUrl}/opportunities/${meta.copy_from_uuid}/copy`,
        { ...variables, updated_by: meta.updated_by }
      )
      return {
        data: { ...response.data, id: response.data.uuid },
      }
    }
    if (resource === 'opportunities' && meta?.generateProfileUrl) {
      const response = await axios.post(
        `${apiUrl}/${resource}${meta.generateProfileUrl}`,
        {
          ...variables,
        },
        {
          params: { updated_by: meta?.updated_by },
        }
      )
      return {
        data: response.data,
      }
    }
    if (resource === 'opportunities' && meta?.generateProfilesUrl) {
      const response = await axios.post(
        `${apiUrl}/${resource}${meta.generateProfilesUrl}`,
        {
          ...variables,
        },
        {
          params: { updated_by: meta?.updated_by },
        }
      )
      return {
        data: response.data,
      }
    }

    if (resource === 'admin-comments' && meta?.funder_uuid) {
      const response = await axios.post(
        `${apiUrl}/funders/${resource}/${meta.funder_uuid}`,
        variables
      )
      return {
        data: response.data,
      }
    }
    if (resource === 'admin-comments' && meta?.opportunity_uuid) {
      const response = await axios.post(
        `${apiUrl}/opportunities/${resource}/${meta.opportunity_uuid}`,
        variables
      )
      return {
        data: response.data,
      }
    }
    // GH #1723: A/B comparison verdicts and shadow-run triggers. Both sit under
    // /admin/match-runs and take no trailing slash.
    if (
      resource === 'match-runs/feedback' ||
      resource === 'match-runs/trigger'
    ) {
      const response = await axios.post(`${apiUrl}/${resource}`, variables)
      return {
        data: response.data,
      }
    }
    if (resource === 'adjust-match-score') {
      const response = await axios.post(
        `${apiUrl}/scan-results/${resource}`,
        variables
      )
      return {
        data: response.data,
      }
    }
    if (resource === 'org-children') {
      const response = await axios.post(`${apiUrl}/orgs/${resource}`, variables)
      return {
        data: response.data,
      }
    }

    if (resource === 'merge-regions') {
      const response = await axios.post(`${apiUrl}/regions/merge`, {
        ...variables,
      })
      return {
        data: response.data,
      }
    }
    if (resource === 'feedback/comment') {
      const response = await axios.post(`${apiUrl}/feedback/comment`, variables)
      return {
        data: response.data,
      }
    }
    if (resource === 'region-sets') {
      const response = await axios.post(`${apiUrl}/regions/sets`, variables)
      return {
        data: response.data,
      }
    }
    // No special handling needed for feedback - the base API URL already includes /admin
    const response = await axios.post(
      `${apiUrl}/${resource}/`,
      {
        ...variables,
      },
      {
        params: { updated_by: meta?.updated_by },
      }
    )
    return {
      data: response.data,
    }
  },

  update: async ({ resource, id, variables, meta }) => {
    if (resource === 'admin-comments' && meta?.funder_uuid) {
      const response = await axios.patch(
        `${apiUrl}/funders/${resource}/${meta.funder_uuid}`,
        variables
      )
      return {
        data: response.data,
      }
    }
    if (resource === 'admin-comments' && meta?.opportunity_uuid) {
      const response = await axios.patch(
        `${apiUrl}/opportunities/${resource}/${meta.opportunity_uuid}`,
        variables
      )
      return {
        data: response.data,
      }
    }
    if (resource === 'account') {
      const response = await axios.patch(
        `${apiUrl}/orgs/${resource}/${id}`,
        variables
      )
      return {
        data: response.data,
      }
    }
    if (resource === 'region-sets') {
      const response = await axios.patch(
        `${apiUrl}/regions/sets/${id}`,
        variables
      )
      return {
        data: response.data,
      }
    }

    // No special handling needed for feedback - the base API URL already includes /admin

    const response = await axios.patch(
      `${apiUrl}/${resource}/${id}`,
      {
        ...variables,
      },
      {
        params: { updated_by: meta?.updated_by },
      }
    )

    return {
      data: response.data,
    }
  },

  deleteOne: async ({ resource, id, meta }) => {
    if (resource === 'funder-regions' || resource === 'opportunity-regions') {
      const response = await axios.delete(
        `${apiUrl}/${resource}/${id}/${meta?.region_id}`,
        { params: { updated_by: meta?.updated_by } }
      )
      return {
        data: response.data,
      }
    }

    if (resource === 'org-children') {
      const response = await axios.delete(`${apiUrl}/orgs/${resource}/${id}`, {
        params: { updated_by: meta?.updated_by },
      })
      return {
        data: response.data,
      }
    }
    if (resource === 'region-sets') {
      const response = await axios.delete(`${apiUrl}/regions/sets/${id}`)
      return {
        data: response.data,
      }
    }

    if (resource === 'memories' && meta?.hardDelete) {
      const response = await axios.delete(`${apiUrl}/${resource}/${id}/hard`, {
        params: { updated_by: meta?.updated_by },
      })
      return {
        data: response.data,
      }
    }

    if (resource === 'users-orgs') {
      const response = await axios.delete(`${apiUrl}/${resource}/${id}`, {
        params: {
          updated_by: meta?.updated_by,
          ...(meta?.force ? { force: true } : {}),
        },
      })
      return {
        data: response.data,
      }
    }

    // No special handling needed for feedback - the base API URL already includes /admin

    const response = await axios.delete(`${apiUrl}/${resource}/${id}`, {
      params: { updated_by: meta?.updated_by },
    })
    return {
      data: response.data,
    }
  },

  getApiUrl: () => {
    if (!apiUrl) {
      throw new Error('API URL is not defined')
    }
    return apiUrl
  },
}
